'use client'

import { useState, useRef, useEffect } from 'react'

export default function Home() {
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [funasrStatus, setFunasrStatus] = useState('')
  const [error, setError] = useState('')
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const startRecording = async () => {
    try {
      setError('')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      
      let mimeType = 'audio/webm'
      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4'
        } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
          mimeType = 'audio/ogg'
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
    } catch (err: any) {
      console.error('录音失败:', err)
      setError(err.message || '无法访问麦克风，请检查权限设置')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  const pollFunasrResult = async (requestId: string) => {
    try {
      const response = await fetch(`https://audio.bluebone.cc/api/funasr/${requestId}`)
      const data = await response.json()

      if (data.status === 'completed') {
        setFunasrStatus('✅ 方言识别完成')
        setResult((prev: any) => ({
          ...prev,
          funasr: {
            ...prev.funasr,
            text: data.text,
            dialect: data.dialect,
            confidence: data.confidence,
            duration: data.duration,
            status: 'completed'
          }
        }))
        
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      } else if (data.status === 'failed') {
        setFunasrStatus('⚠️ 方言识别失败')
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      } else if (data.status === 'processing' || data.status === 'submitting') {
        setFunasrStatus('⏳ 方言识别中...')
      }
    } catch (err) {
      console.error('查询 Fun-ASR 失败:', err)
    }
  }

  const analyzeAudio = async () => {
    if (!audioBlob) return

    setLoading(true)
    setResult(null)
    setFunasrStatus('')
    setError('')

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    const formData = new FormData()
    formData.append('audio', audioBlob, 'recording.webm')

    try {
      const response = await fetch('https://audio.bluebone.cc/api/analyze', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`分析失败 (HTTP ${response.status}): ${errorText.substring(0, 100)}`)
      }

      const data = await response.json()
      setResult(data)

      if (data.funasr?.status === 'processing' && data.request_id) {
        setFunasrStatus('⏳ 方言识别中...')
        
        pollingIntervalRef.current = setInterval(() => {
          pollFunasrResult(data.request_id)
        }, 2000)

        setTimeout(() => {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
            setFunasrStatus('⏱️ 方言识别超时')
          }
        }, 30000)
      }
    } catch (err: any) {
      console.error('分析失败:', err)
      setError(err.message || '分析失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          🎤 音频分析器
        </h1>
        <p className="text-center text-gray-600 mb-8">
          SenseVoice + CLAP + Fun-ASR-Nano
        </p>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4">🎤 录音</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              ❌ {error}
            </div>
          )}
          
          <div className="flex gap-4 mb-4">
            <button
              onClick={startRecording}
              disabled={isRecording}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {isRecording ? '🔴 录音中...' : '🎤 开始录音'}
            </button>
            
            <button
              onClick={stopRecording}
              disabled={!isRecording}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              ⏹️ 停止录音
            </button>
          </div>

          {audioBlob && (
            <div className="mb-4">
              <p className="text-green-600 mb-2">✅ 录音完成（{(audioBlob.size / 1024).toFixed(1)} KB）</p>
              <audio 
                controls 
                src={URL.createObjectURL(audioBlob)} 
                className="w-full"
              />
            </div>
          )}

          <button
            onClick={analyzeAudio}
            disabled={!audioBlob || loading}
            className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-lg font-semibold"
          >
            {loading ? '🔄 分析中...' : '🚀 开始分析'}
          </button>
        </div>

        {result && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4">📊 分析结果</h2>
            
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">⏱️ 处理时间</h3>
              <p className="text-lg">{result.processing_time} 秒</p>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">🎤 SenseVoice（情感识别）</h3>
              <p className="mb-2"><strong>文本:</strong> {result.sensevoice.text || '(无)'}</p>
              <p className="mb-2"><strong>语言:</strong> {result.sensevoice.language}</p>
              
              {result.sensevoice.emotions.all && result.sensevoice.emotions.all.length > 0 ? (
                <div className="mb-4">
                  <p className="font-semibold mb-2">情绪（Top 5）:</p>
                  {result.sensevoice.emotions.all.slice(0, 5).map(([emotion, score]: [string, number]) => (
                    <div key={emotion} className="flex items-center mb-2">
                      <span className="w-20">{emotion}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-4 mx-2">
                        <div
                          className="bg-blue-500 h-4 rounded-full"
                          style={{ width: `${score * 100}%` }}
                        />
                      </div>
                      <span className="w-16 text-right">{(score * 100).toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 mb-4">未检测到明显情绪</p>
              )}

              {result.sensevoice.audio_events.detected.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">检测到的音频事件:</p>
                  {result.sensevoice.audio_events.detected.map(([event, score]: [string, number]) => (
                    <span key={event} className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full mr-2 mb-2">
                      {event} ({(score * 100).toFixed(0)}%)
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">🎵 CLAP（音色识别）</h3>
              
              <div className="mb-4">
                <p className="font-semibold mb-2">音色类型（Top 5）:</p>
                {result.clap.voice_types.all.slice(0, 5).map(([voice, score]: [string, number]) => (
                  <div key={voice} className="flex items-center mb-2">
                    <span className="w-24">{voice}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 mx-2">
                      <div
                        className="bg-purple-500 h-4 rounded-full"
                        style={{ width: `${score * 100}%` }}
                      />
                    </div>
                    <span className="w-16 text-right">{(score * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>

              <div className="mb-4">
                <p className="font-semibold mb-2">说话方式（Top 5）:</p>
                {result.clap.speaking_styles.all.slice(0, 5).map(([style, score]: [string, number]) => (
                  <div key={style} className="flex items-center mb-2">
                    <span className="w-24">{style}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 mx-2">
                      <div
                        className="bg-pink-500 h-4 rounded-full"
                        style={{ width: `${score * 100}%` }}
                      />
                    </div>
                    <span className="w-16 text-right">{(score * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                🌐 Fun-ASR-Nano（方言识别）
                {funasrStatus && <span className="text-sm text-gray-600">{funasrStatus}</span>}
              </h3>
              {result.funasr.status === 'completed' && result.funasr.text ? (
                <>
                  <p className="mb-2"><strong>文本:</strong> {result.funasr.text}</p>
                  <p className="mb-2"><strong>方言:</strong> <span className="text-lg font-bold text-blue-600">{result.funasr.dialect}</span></p>
                  <p className="mb-2"><strong>置信度:</strong> {(result.funasr.confidence * 100).toFixed(1)}%</p>
                </>
              ) : result.funasr.status === 'processing' ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                  <p className="text-gray-600">正在识别方言，请稍候...</p>
                </div>
              ) : result.funasr.status === 'failed' ? (
                <p className="text-orange-500">⚠️ 识别失败</p>
              ) : (
                <p className="text-gray-500">
                  方言: <span className="font-bold">{result.funasr.dialect || '未识别'}</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
